import dynamic from "next/dynamic"
import { componentRegistry, hasComponent } from "./componentRegistry"

/**
 * Dynamic component loader for custom matrix components
 * Uses build-time generated component registry for better webpack compatibility
 */

/**
 * Create error fallback component
 * @param {string} componentName - Name of the component that failed to load
 * @returns {React.Component} - Fallback component
 */
function createErrorFallback(componentName) {
    return function ComponentNotFound() {
        console.warn(`Component "${componentName}" not found`)
        return null
    }
}

// One dynamic() wrapper per component name. Callers invoke the loader during render, so a fresh
// wrapper each time would be a new component type on every re-render (e.g. a shallow URL change),
// remounting the block and blanking it until the import resolves.
const loadedComponents = new Map()

/**
 * Load a custom component dynamically using the build-time generated registry
 * @param {string} componentName - Name of the component to load
 * @returns {React.Component} - Dynamically loaded component
 */
export function loadCustomComponent(componentName) {
    if (!loadedComponents.has(componentName)) {
        loadedComponents.set(componentName, createDynamicComponent(componentName))
    }
    return loadedComponents.get(componentName)
}

function createDynamicComponent(componentName) {
    // Check if component exists in registry
    if (hasComponent(componentName)) {
        return dynamic(
            () =>
                componentRegistry[componentName]().catch(err => {
                    // error loading component, return fallback
                    return { default: createErrorFallback(componentName) }
                }),
            {
                ssr: true,
            },
        )
    }

    // Component not found
    return dynamic(() => Promise.resolve({ default: createErrorFallback(componentName) }))
}

/**
 * Preload a component for better performance
 * @param {string} componentName - Name of the component to preload
 */
export async function preloadComponent(componentName) {
    try {
        const component = loadCustomComponent(componentName)
        // Trigger the dynamic import
        await component.preload?.()
    } catch (error) {
        console.warn(`Failed to preload component ${componentName}:`, error)
    }
}

/**
 * Get all available components (for debugging/development)
 * @returns {string[]} Array of component names
 */
export function getAvailableComponents() {
    return Object.keys(componentRegistry)
}

export default loadCustomComponent
